import React from "react";
import Card from "@amzn/meridian/card";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import { InspectionListView } from "src/services/inspectionService";
import { formatDate } from "src/utils/dateUtil";

type HistoryInspectionCardProps = {
  inspection: InspectionListView;
  onClick: (inspection: InspectionListView) => void;
};

/**
 * Computes the compliance percentage for the card label.
 *
 * questionCount is the number of scored (non-N/A) questions. When there are no
 * scored questions, treat the inspection as fully compliant (100%) — there is
 * nothing that could fail — mirroring the review page, which treats an all-N/A
 * group as compliant.
 */
const compliancePercent = (compliantCount: number, questionCount: number): number => {
  if (questionCount === 0) return 100;
  return Math.round((compliantCount / questionCount) * 100);
};

/**
 * A single Inspection History card: the submission timestamp on top (same
 * createdAt shown on Home) and the compliance outcome below — green
 * "Compliant <pct>%" when every scored question passed, red "Non Compliant"
 * otherwise.
 */
const HistoryInspectionCard = ({ inspection, onClick }: HistoryInspectionCardProps) => {
  const { compliantCount, questionCount } = inspection.complianceStats;
  const percent = compliancePercent(compliantCount, questionCount);
  const isCompliant = compliantCount >= questionCount;
  const { datePart, timePart } = formatDate(inspection.createdAt);

  return (
    <Card onClick={() => onClick(inspection)} spacingInset="400">
      <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="300">
        <Column spacing="200">
          <Row spacing="200" alignmentVertical="center" wrap="down">
            <Text type="b200" color="primary">
              <b>{datePart},</b>
            </Text>
            <Text type="b200" color="secondary">
              {timePart}
            </Text>
          </Row>
          {isCompliant ? (
            <Row spacing="100" alignmentVertical="center">
              <Text type="h300" color="success">
                Compliant
              </Text>
              <Text type="h300" color="secondary">
                {percent}%
              </Text>
            </Row>
          ) : (
            <Text type="h300" color="error">
              Non Compliant
            </Text>
          )}
        </Column>
        <Icon tokens={chevronRightSmallTokens} color="primary" />
      </Row>
    </Card>
  );
};

export default HistoryInspectionCard;
